import clsx from 'clsx';
import { forwardRef, type Ref } from 'react';
import { FormattedMessage } from 'react-intl';

import { DataTestId } from '../../../../core/types/data-props.js';
import { StylingProps } from '../../../../core/types/styling-props.js';
import { WithChildren } from '../../../../core/types/with-children.js';
import { canReceiveDefaultTextFormat as _canReceiveDefaultTextFormat } from '../../../../core/utils/can-receive-default-text-format.js';
import { Flex } from '../../../../layouts/flex/Flex.js';
import { Text } from '../../../../typography/text/Text.js';
import { getDataTableBorderWidth } from '../../../utils/data-table-border-width.js';
import { useDataTable } from '../../contexts/DataTableContext.js';
import { dataTableMessages } from '../../messages.js';
import * as styles from './DataTableSelectionChip.sty.js';

/**
 * Props for the `DataTableSelectionChip` component.
 * @public
 */
export type DataTableSelectionChipProps = WithChildren &
  StylingProps &
  DataTestId;

function DataTableSelectionChipInternal(
  props: DataTableSelectionChipProps,
  forwardedRef: Ref<HTMLDivElement>,
) {
  const {
    children,
    'data-testid': dataTestId,
    className: consumerClassName,
    style: consumerStyle,
    ...remainingProps
  } = props;

  const table = useDataTable();
  const rowsCount = table.getSelectionCount();

  return (
    <Flex
      ref={forwardedRef}
      className={
        styles.selectionChipWrapper[
          table.atoms.visualVariantConfig?.get()?.rowDensity
        ]
      }
      style={{
        marginLeft: table.atoms.visualVariantConfig?.get()?.contained
          ? getDataTableBorderWidth()
          : 0,
      }}
    >
      <Flex
        data-testid={dataTestId}
        className={clsx([styles.selectionChip, consumerClassName])}
        style={consumerStyle}
        {...remainingProps}
      >
        {rowsCount}
      </Flex>
      {!_canReceiveDefaultTextFormat(children) ? (
        children
      ) : (
        <Text as="p" textStyle="base">
          {children ?? (
            <FormattedMessage {...dataTableMessages.selectedTotalRows} />
          )}
        </Text>
      )}
    </Flex>
  );
}

/**
 * A component that displays the number of selected rows in a chip.
 * It can be used in the `DataTable.SelectedRowsActions` component to show how many rows are currently selected.
 * @public
 */
export const DataTableSelectionChip = /* @__PURE__ */ forwardRef(
  DataTableSelectionChipInternal,
);

(
  DataTableSelectionChip as typeof DataTableSelectionChip & {
    displayName: string;
  }
).displayName = 'DataTable.SelectionChip';
